import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Stage } from "@/components/Stage";
import { Wordmark, Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];
const CALENDLY = "https://calendly.com/terryd-lexafide/30min";

const WALKAWAY = [
  "Your missed-call math — what unanswered calls cost your firm",
  "Your hours-reclaimed number — what Lexafide takes off your plate",
  "The first agent to deploy — where to start for fastest payoff",
];

const CREDS = ["Co-founder, Litify", "ex-Salesforce", "20 yrs in legal ops"];

export const CloseSlide = ({ slide }) => {
  const reduce = useReducedMotion();
  const [count, setCount] = useState(50000);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setCount((c) => c + Math.floor(Math.random() * 3) + 1);
    }, 4200);
    return () => clearInterval(id);
  }, [reduce]);

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.13, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  return (
    <Stage testId={`slide-${slide?.n ?? 11}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(32px,7%,110px)] py-[clamp(20px,4%,44px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Wordmark size="sm" />
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            11 / 11
          </span>
        </motion.div>

        {/* Center */}
        <div className="flex-1 min-h-0 flex flex-col justify-center">
          <motion.div variants={item}>
            <Eyebrow>Let's begin</Eyebrow>
          </motion.div>
          <motion.h1
            variants={item}
            className={`font-display font-semibold text-5xl sm:text-6xl lg:text-7xl leading-[1.02] accent-engrave mt-4 ${
              reduce ? "" : "accent-sheen"
            }`}
          >
            Let's map your operations.
          </motion.h1>
          <motion.p
            variants={item}
            className="font-sans text-base text-ink-soft leading-relaxed mt-5 max-w-[44ch]"
          >
            <span className="font-semibold text-ink">No slides. No pitch deck.</span> A
            30-minute working session — we'll find where your firm is leaking hours
            and cases, and show you exactly which agent fixes it.
          </motion.p>

          {/* Two columns */}
          <div className="grid grid-cols-1 min-[900px]:grid-cols-2 gap-x-[clamp(32px,5%,72px)] gap-y-8 mt-9">
            {/* LEFT */}
            <motion.div variants={item}>
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-book"
                className="group inline-flex items-center gap-2.5 bg-ink text-paper-white font-sans text-[15px] font-medium px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-accent"
              >
                Book 30 minutes with Terry
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <p className="font-mono text-[11px] text-ink-muted mt-3">
                Talk to the person who built it — not a sales team.
              </p>

              <div className="mt-7">
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted mb-3">
                  What you'll walk away with
                </div>
                <div className="space-y-2.5">
                  {WALKAWAY.map((w, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                      <span className="font-sans text-[13px] text-ink-soft leading-snug">
                        {w}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* RIGHT — founder card */}
            <motion.div
              variants={item}
              data-testid="founder-card"
              className="rounded-2xl p-6 text-paper bg-[linear-gradient(155deg,#191622_0%,#211D2A_100%)]"
            >
              <div className="flex items-center gap-3.5">
                <span className="w-12 h-12 rounded-full bg-[linear-gradient(135deg,#1E5BFF,#6FA0FF)] flex items-center justify-center text-white font-display font-semibold text-xl shrink-0">
                  T
                </span>
                <div>
                  <div className="font-sans text-base font-medium text-paper">
                    Terry Dohrmann
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.1em] text-[#93B4FF]">
                    CEO &amp; Co-founder
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {CREDS.map((c) => (
                  <span
                    key={c}
                    className="font-mono text-[10px] uppercase tracking-[0.08em] text-paper/70 border border-white/15 rounded-full px-2.5 py-1"
                  >
                    {c}
                  </span>
                ))}
              </div>
              <blockquote className="mt-5 border-l-2 border-accent pl-4 font-display italic text-[15px] text-paper/85 leading-relaxed">
                Built by people who've been inside the matrix — two decades inside
                how law firms actually run.
              </blockquote>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <motion.div
          variants={item}
          className="border-t border-hairline pt-4 mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <p className="font-display text-lg sm:text-xl leading-snug text-ink">
            Your name is on the door.{" "}
            <span className="text-accent">Every call should sound like it.</span>
          </p>
          <span className="inline-flex items-center gap-2 shrink-0">
            <span className="relative flex w-2 h-2">
              {!reduce ? (
                <span className="live-ping absolute inline-flex w-full h-full rounded-full bg-accent" />
              ) : null}
              <span className="relative inline-flex w-2 h-2 rounded-full bg-accent" />
            </span>
            <span
              data-testid="live-counter"
              className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft tabular-nums"
            >
              {count.toLocaleString("en-US")}+ callers handled · and counting
            </span>
          </span>
        </motion.div>
      </motion.div>
    </Stage>
  );
};

export default CloseSlide;
